import React, { useEffect, useMemo, useState } from 'react'
import { Button, Card, CardBody, Col, FormGroup, Input, InputGroup, InputGroupAddon, Label, Row } from 'reactstrap'
import TitlePage from 'components/atoms/title-page'
import InvoiceLogo from 'assets/img/invoice-logo.webp'
import InboundJourneyApiV2 from 'api/v2/admins/inbound-journey-api-v2'
import {
  extractPhase3Deal,
  extractPhase3Deals,
  extractPhase3Products,
  formatPhase3InvoiceDate,
  formatPhase3Money,
  getPhase3CartItems,
  getPhase3ErrorMessage,
  getPhase3InvoiceLineFromProduct,
  getPhase3UserAddress,
  getPhase3UserName,
  getPhase3UserPhone,
  normalizePhase3ImageUrl
} from '../helpers/phase3-inbound-journey-helper'

const STORAGE_KEY = 'phase3-invoice-draft'

const defaultInvoice = {
  displayHeaderAndFooter  : true,
  displayPrices           : true,
  displayCustomerName     : true,
  displayCustomerAddress  : true,
  displayCustomerPhone    : true,
  footerAddress           : 'id',
  customerName            : '',
  customerAddress         : '',
  customerPhone           : '',
  invoiceDate             : new Date().toISOString().slice(0, 10),
  referenceNumber         : '-',
  clientAdvisor           : '',
  shippedVia              : '',
  downPayment             : '',
  pageTitle               : '',
  invoiceItemList         : []
}

const formatAmount = (value) => {
  const number = parseInt(value || 0, 10)
  if (Number.isNaN(number)) return 'N/A'
  return number.toLocaleString('en-US')
}

const getTotalSpend = (items, downPayment) => {
  let total = 0
  let currency = 'IDR'

  items.forEach((item) => {
    currency = item.currency || currency
    const qty = Math.max(1, parseInt(item.quantity || 1, 10))
    const amount = Math.max(0, parseInt(item.amount || 0, 10))
    total += qty * amount
  })

  total -= parseInt(downPayment || 0, 10)
  return { amount: total, currency }
}

const Phase3InvoicePrintPage = ({ pageUtils, apiNamespace = 'inbound' }) => {
  const [ invoice, setInvoice ] = useState(defaultInvoice)
  const [ dealSearchTerm, setDealSearchTerm ] = useState('')
  const [ productSearchTerm, setProductSearchTerm ] = useState('')
  const [ dealList, setDealList ] = useState([])
  const [ productList, setProductList ] = useState([])
  const [ savedMarker, setSavedMarker ] = useState('')

  useEffect(() => {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return

    try {
      const parsed = JSON.parse(raw)
      setInvoice({ ...defaultInvoice, ...parsed })
      setSavedMarker(JSON.stringify(parsed.invoiceItemList || []))
    } catch (error) {
      // Ignore corrupt local draft.
    }
  }, [])

  useEffect(() => {
    document.title = invoice.pageTitle || 'Invoice'
  }, [ invoice.pageTitle ])

  useEffect(() => {
    const onPrintShortcut = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'p') {
        const currentMarker = JSON.stringify(invoice.invoiceItemList || [])
        if (savedMarker && savedMarker !== currentMarker) {
          event.preventDefault()
          alert('Mohon klik Simpan Draft sebelum melakukan print')
        }
      }
    }

    window.addEventListener('keydown', onPrintShortcut)
    return () => window.removeEventListener('keydown', onPrintShortcut)
  }, [ invoice.invoiceItemList, savedMarker ])

  const setField = (key, value) => setInvoice((prev) => ({ ...prev, [key]: value }))

  const setItemField = (index, key, value) => {
    setInvoice((prev) => {
      const nextItems = [ ...(prev.invoiceItemList || []) ]
      nextItems[index] = { ...nextItems[index], [key]: value }
      return { ...prev, invoiceItemList: nextItems }
    })
  }

  const searchDeals = async () => {
    try {
      const response = await InboundJourneyApiV2.searchDeals({ q: dealSearchTerm })
      setDealList(extractPhase3Deals(response))
    } catch (error) {
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
    }
  }

  const openDeal = async (dealId) => {
    try {
      const response = await InboundJourneyApiV2.showDeal({ dealId })
      const deal = extractPhase3Deal(response)
      const user = deal.user || deal.customer || {}
      const cartItems = getPhase3CartItems(deal)

      setInvoice((prev) => ({
        ...prev,
        referenceNumber : deal.reference_number || deal.payment_reference_number || '-',
        shippedVia      : deal.shipped_via || deal.shipment_method || prev.shippedVia,
        clientAdvisor   : deal.sales_name_in_invoice || deal.deal_by || prev.clientAdvisor,
        downPayment     : deal.down_payment || prev.downPayment,
        customerName    : getPhase3UserName(user) || deal.user_name || prev.customerName,
        customerAddress : getPhase3UserAddress(user) || deal.user_address || prev.customerAddress,
        customerPhone   : getPhase3UserPhone(user) || deal.user_phone || prev.customerPhone,
        invoiceItemList : cartItems.map((cartItem) => getPhase3InvoiceLineFromProduct(cartItem.product || {}, cartItem))
      }))
      setDealList([])
      setDealSearchTerm('')
    } catch (error) {
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
    }
  }

  const searchProducts = async () => {
    try {
      const response = await InboundJourneyApiV2.getProducts({ q: productSearchTerm, per_page: 10, apiNamespace })
      setProductList(extractPhase3Products(response))
    } catch (error) {
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
    }
  }

  const addProduct = (product) => {
    setInvoice((prev) => ({
      ...prev,
      invoiceItemList: [
        ...(prev.invoiceItemList || []),
        getPhase3InvoiceLineFromProduct(product, { quantity: 1, price: product.selling_price })
      ]
    }))
    setProductList([])
    setProductSearchTerm('')
  }

  const removeItem = (index) => {
    setInvoice((prev) => ({
      ...prev,
      invoiceItemList: prev.invoiceItemList.filter((_, i) => i !== index)
    }))
  }

  const saveDraft = () => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(invoice))
    setSavedMarker(JSON.stringify(invoice.invoiceItemList || []))
    pageUtils?.setAlertMsg?.('Invoice draft saved locally')
  }

  const totalSpend = useMemo(() => getTotalSpend(invoice.invoiceItemList, invoice.downPayment), [ invoice.invoiceItemList, invoice.downPayment ])

  return (
    <>
      <style>{`
        @media print {
          .phase3-print-hidden { display: none !important; }
          .navbar, .sidenav, .backdrop { display: none !important; }
          .main-content { margin-left: 0 !important; }
          .container-fluid { padding: 0 !important; margin: 0 !important; max-width: none !important; }
          .phase3-invoice-preview { width: 100% !important; margin: 0 !important; }
        }
        .phase3-invoice-layout { display: flex; align-items: flex-start; justify-content: space-around; gap: 24px; }
        .phase3-invoice-control { max-width: 520px; width: 38%; }
        .phase3-invoice-preview { background: #fff; width: 60%; min-width: 650px; padding: 24px 38px; color: #000; }
        .phase3-invoice-preview table { width: 100%; table-layout: fixed; font-size: 7px; }
        .phase3-invoice-preview th { padding-bottom: 7px; border-bottom: 1px solid #000; }
        .phase3-invoice-preview td { padding-top: 7px; vertical-align: top; }
        .phase3-invoice-footer-row td { border-top: 1px solid #000; font-weight: bold; }
        .phase3-invoice-image { width: 90px; max-height: 120px; overflow: hidden; }
        .phase3-invoice-image img { max-width: 90px; }
        .phase3-invoice-stamp-image { width: 90px; max-height: 155px; overflow: hidden; margin-top: 4px; }
        .phase3-invoice-stamp-image img { max-width: 90px; }
      `}</style>

      <div className='phase3-print-hidden'>
        <TitlePage mainTitle='Inbound Journey' subTitle='Invoice Print' />
      </div>

      <div className='phase3-invoice-layout'>
        <div className='phase3-print-hidden phase3-invoice-control'>
          <Card>
            <CardBody>
              <h4>Invoice</h4>

              <Row form>
                <Col md='6'>
                  <FormGroup check>
                    <Label check><Input type='checkbox' checked={ invoice.displayHeaderAndFooter } onChange={ () => setField('displayHeaderAndFooter', !invoice.displayHeaderAndFooter) } /> Display Header & Footer</Label>
                  </FormGroup>
                </Col>
                <Col md='6'>
                  <FormGroup check>
                    <Label check><Input type='checkbox' checked={ invoice.displayPrices } onChange={ () => setField('displayPrices', !invoice.displayPrices) } /> Display Prices</Label>
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup className='mt-3'>
                <Label>Search Deal</Label>
                <InputGroup>
                  <Input value={ dealSearchTerm } onChange={ (event) => setDealSearchTerm(event.target.value) } placeholder='Reference number / buyer / SKU' />
                  <InputGroupAddon addonType='append'><Button color='primary' onClick={ searchDeals }>Search</Button></InputGroupAddon>
                </InputGroup>
              </FormGroup>

              { dealList.map((deal) => (
                <div key={ deal.id } className='border p-2 mb-1 cursor-pointer' role='button' onClick={ () => openDeal(deal.id) }>
                  <div className='font-weight-bold'>{ deal.reference_number || deal.payment_reference_number || `Deal #${deal.id}` }</div>
                  <div className='small text-muted'>{ deal.user_name || deal.user?.email || deal.state || '-' }</div>
                </div>
              )) }

              <Row form>
                <Col md='9'>
                  <FormGroup>
                    <Label>Customer Name</Label>
                    <Input value={ invoice.customerName } onChange={ (event) => setField('customerName', event.target.value) } />
                  </FormGroup>
                </Col>
                <Col md='3' className='d-flex align-items-center'>
                  <FormGroup check><Label check><Input type='checkbox' checked={ invoice.displayCustomerName } onChange={ () => setField('displayCustomerName', !invoice.displayCustomerName) } /> Show</Label></FormGroup>
                </Col>
              </Row>

              <Row form>
                <Col md='9'>
                  <FormGroup>
                    <Label>Customer Address</Label>
                    <Input type='textarea' value={ invoice.customerAddress } onChange={ (event) => setField('customerAddress', event.target.value) } />
                  </FormGroup>
                </Col>
                <Col md='3' className='d-flex align-items-center'>
                  <FormGroup check><Label check><Input type='checkbox' checked={ invoice.displayCustomerAddress } onChange={ () => setField('displayCustomerAddress', !invoice.displayCustomerAddress) } /> Show</Label></FormGroup>
                </Col>
              </Row>

              <Row form>
                <Col md='9'>
                  <FormGroup>
                    <Label>Customer Phone</Label>
                    <Input value={ invoice.customerPhone } onChange={ (event) => setField('customerPhone', event.target.value) } />
                  </FormGroup>
                </Col>
                <Col md='3' className='d-flex align-items-center'>
                  <FormGroup check><Label check><Input type='checkbox' checked={ invoice.displayCustomerPhone } onChange={ () => setField('displayCustomerPhone', !invoice.displayCustomerPhone) } /> Show</Label></FormGroup>
                </Col>
              </Row>

              <Row form>
                <Col md='6'><FormGroup><Label>Invoice Date</Label><Input type='date' value={ invoice.invoiceDate } onChange={ (event) => setField('invoiceDate', event.target.value) } /></FormGroup></Col>
                <Col md='6'><FormGroup><Label>Reference Number</Label><Input value={ invoice.referenceNumber } onChange={ (event) => setField('referenceNumber', event.target.value) } /></FormGroup></Col>
              </Row>

              <Row form>
                <Col md='6'><FormGroup><Label>Client Advisor</Label><Input value={ invoice.clientAdvisor } onChange={ (event) => setField('clientAdvisor', event.target.value) } /></FormGroup></Col>
                <Col md='6'><FormGroup><Label>Shipped Via</Label><Input value={ invoice.shippedVia } onChange={ (event) => setField('shippedVia', event.target.value) } /></FormGroup></Col>
              </Row>

              <FormGroup><Label>Down Payment</Label><Input type='number' value={ invoice.downPayment } onChange={ (event) => setField('downPayment', event.target.value) } /></FormGroup>

              <FormGroup>
                <Label>Search Product</Label>
                <InputGroup>
                  <Input value={ productSearchTerm } onChange={ (event) => setProductSearchTerm(event.target.value) } placeholder='SKU / product name' />
                  <InputGroupAddon addonType='append'><Button color='primary' onClick={ searchProducts }>Search</Button></InputGroupAddon>
                </InputGroup>
              </FormGroup>

              { productList.map((product) => (
                <div key={ product.id } className='border p-2 mb-1 cursor-pointer' role='button' onClick={ () => addProduct(product) }>
                  <div className='font-weight-bold'>{ product.sku } - { product.name }</div>
                  <div className='small text-muted'>{ formatPhase3Money(product.selling_price) }</div>
                </div>
              )) }

              { invoice.invoiceItemList.map((item, index) => (
                <div key={ index } className='border rounded p-2 mb-2'>
                  <div className='d-flex justify-content-between align-items-center mb-2'>
                    <strong>#{ index + 1 } { item.sku }</strong>
                    <Button size='sm' color='danger' onClick={ () => removeItem(index) }>Remove</Button>
                  </div>
                  <FormGroup><Label>Title</Label><Input type='textarea' value={ item.title } onChange={ (event) => setItemField(index, 'title', event.target.value) } /></FormGroup>
                  <FormGroup><Label>Description</Label><Input value={ item.description } onChange={ (event) => setItemField(index, 'description', event.target.value) } /></FormGroup>
                  <Row form>
                    <Col md='3'><FormGroup><Label>Qty</Label><Input type='number' min='1' value={ item.quantity } onChange={ (event) => setItemField(index, 'quantity', event.target.value) } /></FormGroup></Col>
                    <Col md='3'><FormGroup><Label>Currency</Label><Input value={ item.currency } onChange={ (event) => setItemField(index, 'currency', event.target.value) } /></FormGroup></Col>
                    <Col md='6'><FormGroup><Label>Unit Price</Label><Input type='number' value={ item.amount } onChange={ (event) => setItemField(index, 'amount', event.target.value) } /></FormGroup></Col>
                  </Row>
                  <FormGroup><Label>Main Image URL / file name</Label><Input value={ item.mainImageUrl || '' } onChange={ (event) => setItemField(index, 'mainImageUrl', event.target.value) } /></FormGroup>
                  <FormGroup><Label>Stamp Image URL / file name</Label><Input value={ item.stampImageUrl || '' } onChange={ (event) => setItemField(index, 'stampImageUrl', event.target.value) } /></FormGroup>
                </div>
              )) }

              <FormGroup><Label>File Title</Label><Input value={ invoice.pageTitle } onChange={ (event) => setField('pageTitle', event.target.value) } /></FormGroup>

              <Button color='primary' onClick={ saveDraft } className='mr-2'>Simpan Draft</Button>
              <Button color='secondary' onClick={ () => window.print() }>Print</Button>
            </CardBody>
          </Card>
        </div>

        <div className='phase3-invoice-preview'>
          <img
            src={ InvoiceLogo }
            alt='Belle and Kate Logo'
            style={{ height: 50, width: 188, display: 'block', margin: '0 auto 14px', visibility: invoice.displayHeaderAndFooter ? 'visible' : 'hidden' }}
          />

          <h2 style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 4 }}>INVOICE</h2>

          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', visibility: invoice.displayCustomerName ? 'visible' : 'hidden' }}><strong style={{ fontSize: 8, minWidth: 73 }}>SOLD TO:</strong><strong style={{ fontSize: 8, marginLeft: 8 }}>{ invoice.customerName }</strong></div>
              <div style={{ display: 'flex', visibility: invoice.displayCustomerAddress ? 'visible' : 'hidden' }}><strong style={{ fontSize: 8, minWidth: 73 }}>ADDRESS:</strong><strong style={{ fontSize: 8, marginLeft: 8, whiteSpace: 'pre-line' }}>{ invoice.customerAddress }</strong></div>
              <div style={{ display: 'flex', visibility: invoice.displayCustomerPhone ? 'visible' : 'hidden' }}><strong style={{ fontSize: 8, minWidth: 73 }}>PHONE NUMBER:</strong><strong style={{ fontSize: 8, marginLeft: 8 }}>{ invoice.customerPhone }</strong></div>
            </div>
            <div style={{ flex: 1, minWidth: 160 }}>
              <div style={{ display: 'flex' }}><strong style={{ fontSize: 8, minWidth: 75 }}>NO:</strong><strong style={{ fontSize: 8, marginLeft: 8 }}>{ invoice.referenceNumber }</strong></div>
              <div style={{ display: 'flex' }}><strong style={{ fontSize: 8, minWidth: 75 }}>DATE:</strong><strong style={{ fontSize: 8, marginLeft: 8 }}>{ formatPhase3InvoiceDate(invoice.invoiceDate) }</strong></div>
              <div style={{ display: 'flex' }}><strong style={{ fontSize: 8, minWidth: 75 }}>CLIENT ADVISOR:</strong><strong style={{ fontSize: 8, marginLeft: 8 }}>{ invoice.clientAdvisor }</strong></div>
              <div style={{ display: 'flex' }}><strong style={{ fontSize: 8, minWidth: 75 }}>SHIPPED VIA:</strong><strong style={{ fontSize: 8, marginLeft: 8 }}>{ invoice.shippedVia }</strong></div>
            </div>
          </div>

          <table style={{ marginTop: 8, marginBottom: 16 }}>
            <colgroup>
              <col style={{ width: '58%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '20%' }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>DESCRIPTION</th>
                <th style={{ textAlign: 'center', visibility: invoice.displayPrices ? 'visible' : 'hidden' }}>QTY</th>
                <th style={{ textAlign: 'right', visibility: invoice.displayPrices ? 'visible' : 'hidden' }}>UNIT PRICE</th>
                <th style={{ textAlign: 'right', visibility: invoice.displayPrices ? 'visible' : 'hidden' }}>AMOUNT</th>
              </tr>
            </thead>
            <tbody>
              { invoice.invoiceItemList.map((item, index) => {
                const qty = Math.max(1, parseInt(item.quantity || 1, 10))
                const unitPrice = Math.max(0, parseInt(item.amount || 0, 10))
                const lineTotal = qty * unitPrice

                return (
                  <tr key={ index }>
                    <td style={{ paddingRight: 10 }}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <div style={{ flexShrink: 0 }}>
                          <div className='phase3-invoice-image'><img alt='' src={ normalizePhase3ImageUrl(item.mainImageUrl) || 'https://placehold.co/1512x2016' } /></div>
                          { item.stampImageUrl && <div className='phase3-invoice-stamp-image'><img alt='' src={ normalizePhase3ImageUrl(item.stampImageUrl) } /></div> }
                        </div>
                        <div>
                          <p style={{ fontWeight: 'bold', marginBottom: 4, whiteSpace: 'pre-line' }}>{ item.title }</p>
                          { String(item.description || '').split(', ').filter(Boolean).map((line, idx) => (
                            <div key={ idx } style={{ display: 'flex', marginBottom: 2 }}>
                              <img src='/invoice-checkmark.svg' alt='' style={{ width: 10, height: 10 }} />
                              <span style={{ marginLeft: 3 }}>{ line }</span>
                            </div>
                          )) }
                        </div>
                      </div>
                    </td>
                    <td style={{ textAlign: 'center', visibility: invoice.displayPrices ? 'visible' : 'hidden' }}>{ qty }</td>
                    <td style={{ textAlign: 'right', visibility: invoice.displayPrices ? 'visible' : 'hidden' }}>IDR { formatAmount(unitPrice) }</td>
                    <td style={{ textAlign: 'right', visibility: invoice.displayPrices ? 'visible' : 'hidden' }}>IDR { formatAmount(lineTotal) }</td>
                  </tr>
                )
              }) }

              { Number(invoice.downPayment) > 0 && (
                <tr>
                  <td />
                  <td />
                  <td style={{ fontWeight: 'bold', textAlign: 'right', visibility: invoice.displayPrices ? 'visible' : 'hidden' }}>DP</td>
                  <td style={{ fontWeight: 'bold', textAlign: 'right', visibility: invoice.displayPrices ? 'visible' : 'hidden' }}>IDR { formatAmount(invoice.downPayment) }</td>
                </tr>
              ) }

              <tr>
                <td style={{ maxWidth: 300, lineHeight: '8px', paddingTop: 7, paddingRight: 10 }}>
                  *Semua barang promo (harga khusus atau special price) yang sudah dibeli di BELLE&amp;KATE Room adalah final. Kami tidak menerima klaim pengembalian dana atau pertukaran barang setelah barang dipesan. Terima kasih!
                </td>
                <td />
                <td />
                <td />
              </tr>
            </tbody>
            <tfoot className='phase3-invoice-footer-row'>
              <tr>
                <td />
                <td />
                <td style={{ textAlign: 'right', visibility: invoice.displayPrices ? 'visible' : 'hidden' }}>Total</td>
                <td style={{ textAlign: 'right', visibility: invoice.displayPrices ? 'visible' : 'hidden' }}>{ totalSpend.currency } { formatAmount(totalSpend.amount) }</td>
              </tr>
            </tfoot>
          </table>

          <div style={{ textAlign: 'center', visibility: invoice.displayHeaderAndFooter ? 'visible' : 'hidden' }}>
            { invoice.footerAddress === 'sg' ? (
              <>
                <p style={{ fontSize: 7, fontWeight: 600, lineHeight: '9px', margin: 0 }}>313@Somerset</p>
                <p style={{ fontSize: 7, fontWeight: 600, lineHeight: '9px', margin: 0 }}>313 Orchard Rd, Level B1 Number 16, Singapore 238895</p>
              </>
            ) : (
              <>
                <p style={{ fontSize: 7, fontWeight: 600, lineHeight: '9px', margin: 0 }}>JL. JEND. SUDIRMAN KAV 52-53. LOT 22, RT 5 / RW 3, SENAYAN.</p>
                <p style={{ fontSize: 7, fontWeight: 600, lineHeight: '9px', margin: 0 }}>KBY. BARU, KOTA JAKARTA SELATAN, DAERAH KHUSUS IBUKOTA JAKARTA 12190</p>
              </>
            ) }
          </div>
        </div>
      </div>
    </>
  )
}

export default Phase3InvoicePrintPage
